import type { UnlikelyCart, UnlikelyCustomAttributes } from "@unlikelystudio/commerce-connector"

import { getStorePublicCredentials } from "~/lib/shopify/client/public"
import { getReferenceIdFromGID } from "~/lib/shopify/utils/id"

export type BabackCart = {
  token: string
  note: string
  attributes: UnlikelyCustomAttributes
  original_total_price: number
  total_price: number
  total_discount: number
  total_weight: number
  item_count: number
  items: BabackBasketItem[]
  requires_shipping: boolean
  currency: string
  items_subtotal_price: number
}

export type BabackBasketItem = {
  id: number
  properties: UnlikelyCustomAttributes
  quantity: number
  variant_id: number
  key: string
  title: string
  price: number
  original_price: number
  discounted_price: number
  line_price: number
  original_line_price: number
  total_discount: number
  discounts: {
    amount: number
    title: string
  }[]
  sku: string
  grams: number
  vendor: string
  taxable: boolean
  product_id: number
  product_has_only_default_variant: boolean
  gift_card: boolean
  final_price: number
  final_line_price: number
  url: string
  featured_image: {
    alt: string
    height: number
    url: string
    width: number
  }
  image: string
  handle: string
  requires_shipping: boolean
  product_type: string
  product_title: string
  product_description: string
  variant_title: string
  variant_options: string[]
  options_with_values: {
    name: string | null
    value: string | null
  }[]
  line_level_total_discount: number
  has_components: boolean
}

export function serializeCartForBaback({
  cart,
  cartArticlesNumber,
  totalPrice,
  totalDiscount,
  currencyCode,
  totalWeight,
}: {
  cart: UnlikelyCart | undefined
  cartArticlesNumber: number | undefined
  totalPrice: number | undefined | null
  totalDiscount: number | undefined
  isFilledCart: boolean | undefined
  cartWebUrl: string | undefined
  currencyCode: string | undefined | null
  cartQueryIsComplete: boolean | undefined
  totalWeight: number | undefined
}) {
  const cartValue: BabackCart = {
    note: cart?.note || "",
    attributes: cart?.attributes || [],
    original_total_price: totalPrice || 0,
    total_price: totalPrice || 0,
    total_discount: totalDiscount || 0,
    total_weight: totalWeight || 0,
    item_count: cartArticlesNumber || 0,
    items:
      cart?.lines.map((item) => {
        return {
          id: getReferenceIdFromGID(item?.merchandise?.id) ?? 0,
          properties: item?.attributes ?? [],
          quantity: item?.quantity ?? 0,
          variant_id: getReferenceIdFromGID(item?.merchandise?.id) ?? 0,
          key: "",
          title: item?.merchandise?.title ?? "",
          price: item?.merchandise?.price?.amount ?? 0,
          original_price: item?.merchandise?.compareAtPrice?.amount ?? 0,
          discounted_price: 0,
          line_price: item?.merchandise?.price?.amount ? item?.merchandise?.price?.amount * item?.quantity : 0,
          original_line_price: 0,
          final_price: 0,
          final_line_price: 0,
          total_discount: item?.discountAllocations?.reduce(
            (acc, curr) => acc + (curr.discountedAmount?.amount ?? 0),
            0
          ),
          discounts: item?.discountAllocations?.map((discount, index) => {
            return {
              amount: discount.discountedAmount?.amount ?? 0,
              title: `discount-${index}`,
            }
          }),
          line_level_total_discount: 0,
          sku: item?.merchandise?.sku ?? "",
          grams: item?.merchandise?.weight?.value ?? 0,
          vendor: item?.merchandise?.product?.vendor ?? "",
          taxable: item?.merchandise?.taxable ?? false,
          product_id: getReferenceIdFromGID(item?.merchandise?.product?.id) ?? 0,
          product_has_only_default_variant: item?.merchandise?.product?.variants?.length === 1,
          gift_card: item?.merchandise?.product?.isGiftCard ?? false,
          url: item?.merchandise?.product?.handle ?? "",
          featured_image: {
            alt: item?.merchandise?.product?.featuredImage?.altText ?? "",
            height: item?.merchandise?.product?.featuredImage?.height ?? 0,
            url: item?.merchandise?.product?.featuredImage?.url ?? "",
            width: item?.merchandise?.product?.featuredImage?.width ?? 0,
          },
          image: item?.merchandise?.image?.url ?? "",
          handle: item?.merchandise?.product?.handle ?? "",
          requires_shipping: !item?.merchandise?.product?.isGiftCard,
          product_type: item?.merchandise?.product?.productType ?? "",
          product_title: item?.merchandise?.product?.title ?? "",
          product_description: item?.merchandise?.product?.description ?? "",
          variant_title: item?.merchandise?.title ?? "",
          variant_options: item?.merchandise?.options ?? [],
          options_with_values: item?.merchandise?.selectedOptions ?? [],
          has_components: item?.merchandise?.requiresComponents ?? false,
        }
      }) ?? [],
    requires_shipping: true,
    currency: currencyCode || "",
    items_subtotal_price: 0,
    token: getStorePublicCredentials().storefrontAccessToken,
  }

  return cartValue
}
